import React, { useEffect, useState, useMemo, useCallback, type SyntheticEvent } from 'react';
import { styled, keyframes } from '@compiled/react';
import { Transition } from 'react-transition-group';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import CustomizeIcon from '@atlaskit/icon/core/migration/customize--preferences';
import LockLockedIcon from '@atlaskit/icon/core/migration/lock-locked--lock-filled';
import LockUnlockedIcon from '@atlaskit/icon/core/migration/lock-unlocked--unlock-filled';
import SidebarExpandIcon from '@atlaskit/icon/core/sidebar-expand';
import SidebarRightIcon from '@atlaskit/icon/core/sidebar-right';
import { ModalTransition } from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useWorkspaceContext } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/controllers/workspace-context/index.tsx';
import { graphModalExperience } from '@atlassian/jira-servicedesk-insight-experiences/src/index.tsx';
import {
	ShowObjectGraphDetailsIcon,
	HideObjectGraphDetailsIcon,
} from '@atlassian/jira-servicedesk-insight-glyphs/src/index.tsx';
import { AsyncLazyForceGraph as AsyncForceGraph } from '@atlassian/jira-servicedesk-insight-graphs-force-graph/src/async.tsx';
import { NODE_TYPE } from '@atlassian/jira-servicedesk-insight-graphs-force-graph/src/common/constants.tsx';
import type { EChartsEvent } from '@atlassian/jira-servicedesk-insight-graphs-force-graph/src/common/types.tsx';
import { usePushingSidebar } from '@atlassian/jira-servicedesk-insight-graphs-sidebar/src/controllers/index.tsx';
import {
	PushingSidebar,
	PushingSidebarContainer,
} from '@atlassian/jira-servicedesk-insight-graphs-sidebar/src/ui/index.tsx';
import { useObjectDetailsService } from '@atlassian/jira-servicedesk-insight-object-services/src/services/object-details/index.tsx';
import { useObjectGraphService } from '@atlassian/jira-servicedesk-insight-object-services/src/services/object-graph/index.tsx';
import type { CmdbReferencedObject } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute-values.tsx';
import { toCmdbObjectId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { GRAPH_NODE_TYPES } from '../common/types.tsx';
import { useObjectGraphConfiguration } from '../controllers/configuration/index.tsx';
import { useSelectedNode } from '../controllers/selected-node/index.tsx';
import { type Action, Actions, ActionsButton } from './actions/index.tsx';
import { GraphConfigurationActionPanel } from './graph-config-panel/dropdown-panel/index.tsx';
import { ReferenceDepthConfigurationInputs } from './graph-config-panel/reference-depth-inputs/index.tsx';
import { ReferenceTypeToggleList } from './graph-config-panel/reference-types-toggle-list/index.tsx';
import { GraphError } from './graph-error/index.tsx';
import { GraphHeader } from './graph-header/index.tsx';
import messages from './messages.tsx';
import { ObjectTypeView } from './object-type-view/index.tsx';
import { ObjectView } from './object-view/index.tsx';
import type { InsightObjectGraphOrNullProps, InsightObjectGraphProps } from './types.tsx';
import {
	getNormalizedGraphData,
	getFilteredEdgesAndNodes,
	getConnectedNodesFromParent,
} from './utils.tsx';

const DIALOG_WIDTH = 1280;
const DIALOG_HEIGHT = 720;
const FADEIN_ANIMATION_DELAY = 500;

export const InsightObjectGraph = ({
	objectId,
	objectTypeId,
	onClose,
	source,
}: InsightObjectGraphProps) => {
	const [selectedNode, { setSelectedObjectNode, setSelectedObjectTypeNode }] =
		useSelectedNode(objectId);
	const { formatMessage } = useIntl();
	const { workspaceId } = useWorkspaceContext();
	const [sidebarState, sidebarActions] = usePushingSidebar({ width: 480 });

	const [isConfigPanelOpen, setIsConfigPanelOpen] = useState<boolean>(false);
	const [isGraphLayoutLocked, setIsGraphLayoutLocked] = useState<boolean>(true);

	const [
		{ data: objectDetailsData, loading: objectDetailsLoading, error: objectDetailsError },
		fetchObjectDetails,
	] = useObjectDetailsService();
	const [{ data: objectGraphData, loading: graphLoading, error: graphError }, fetchObjectGraph] =
		useObjectGraphService();

	const [
		{ referenceTypes, maxOfSameType, referenceDepth },
		{ updateReferenceDepth, updateMaxOfSameType, updateReferenceTypes },
	] = useObjectGraphConfiguration(objectGraphData, objectTypeId);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fetchGraphData = useCallback(
		(objId: string) => {
			if (workspaceId) {
				fetchObjectGraph({
					workspaceId,
					objectId: toCmdbObjectId(objId),
					depth: referenceDepth,
					maxOfSameType,
				});
			}
		},
		[fetchObjectGraph, maxOfSameType, referenceDepth, workspaceId],
	);

	const transformedGraphData = useMemo(() => {
		// the initial graph data response goes through a series of transformations before it renders:
		// 1. we filter the nodes & edges based on the currently selected reference types in the object graph configuration panel
		// 2. we only show nodes that have a connection (either direct or indirect) to the currently selected object based on that filtering
		// 3. we then transform the result of that into a data shape the force graph library expects
		const filteredBySelectedReferenceTypes = getFilteredEdgesAndNodes(
			objectGraphData,
			referenceTypes,
			selectedNode.referenceId,
		);
		const nodesConnectedToSelectedNode = getConnectedNodesFromParent(
			filteredBySelectedReferenceTypes,
			selectedNode.referenceId,
		);
		return getNormalizedGraphData(nodesConnectedToSelectedNode);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [objectGraphData, referenceTypes]); // in this case we do want to explicity exclude the reference id here

	const handleFetchObjectDetails = useCallback(() => {
		if (workspaceId) {
			fetchObjectDetails({
				objectId: toCmdbObjectId(selectedNode.referenceId),
				workspaceId,
			});
		}
	}, [fetchObjectDetails, selectedNode.referenceId, workspaceId]);

	useEffect(() => {
		if (workspaceId) {
			fetchObjectDetails({
				objectId: toCmdbObjectId(selectedNode.referenceId),
				workspaceId,
			});
		}
	}, [fetchObjectDetails, selectedNode.referenceId, workspaceId]);

	useEffect(() => {
		fetchGraphData(objectId);
	}, [fetchGraphData, fetchObjectGraph, objectId, workspaceId]);

	const onNodeClicked = useCallback(
		(selectedObjectData: EChartsEvent) => {
			const { data, dataType } = selectedObjectData;
			const { context } = data;
			if (dataType === NODE_TYPE && context) {
				switch (context.type) {
					case GRAPH_NODE_TYPES.OBJECT:
						setSelectedObjectNode(context.id);
						break;
					case GRAPH_NODE_TYPES.OBJECT_TYPE:
						setSelectedObjectTypeNode(context);
						break;
					default:
						break;
				}
				fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'insightObjectGraphItem', {
					source,
				});
			}
		},
		[createAnalyticsEvent, setSelectedObjectNode, setSelectedObjectTypeNode, source],
	);

	const onNodeDoubleClicked = useCallback(
		(selectedObjectData: EChartsEvent) => {
			const { data, dataType } = selectedObjectData;
			const { context } = data;
			if (dataType === NODE_TYPE && context?.type === GRAPH_NODE_TYPES.OBJECT) {
				fetchGraphData(context.id);
			}
		},
		[fetchGraphData],
	);

	const onObjectAttributeClick = useCallback(
		(object: CmdbReferencedObject) => {
			const { nodes = [] } = transformedGraphData ?? {};
			const isNodeVisibleInGraph = nodes.some((n) => n.context?.id === object.objectId);
			if (isNodeVisibleInGraph) {
				setSelectedObjectNode(object.objectId);
			} else {
				fetchGraphData(object.objectId);
				setSelectedObjectNode(object.objectId);
			}
		},
		[fetchGraphData, setSelectedObjectNode, transformedGraphData],
	);

	const onToggleLockedLayout = () => {
		const shouldLockGraph = !isGraphLayoutLocked;
		setIsGraphLayoutLocked(shouldLockGraph);
	};

	const HideObjectGraphDetailsIconComponent = isVisualRefreshEnabled()
		? SidebarExpandIcon
		: HideObjectGraphDetailsIcon;
	const ShowObjectGraphDetailsIconComponent = isVisualRefreshEnabled()
		? SidebarRightIcon
		: ShowObjectGraphDetailsIcon;
	const actionButtons: Action[] = [
		{
			Icon: isGraphLayoutLocked ? LockLockedIcon : LockUnlockedIcon,
			label: formatMessage(isGraphLayoutLocked ? messages.unlockLayout : messages.lockLayout),
			onClick: onToggleLockedLayout,
			isSelected: isGraphLayoutLocked,
			iconSpacing: 'spacious',
		},
		{
			Icon: sidebarState.isOpen
				? HideObjectGraphDetailsIconComponent
				: ShowObjectGraphDetailsIconComponent,
			testId: 'servicedesk-insight-object-graph.ui.graph-toggle-button',
			label: formatMessage(sidebarState.isOpen ? messages.hideDetail : messages.showDetail),
			onClick: (event: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
				fireUIAnalytics(analyticsEvent, 'insightObjectGraphToggleDetailsPanel', {
					action: sidebarState.isOpen ? 'hiding' : 'showing',
					source,
				});
				if (isConfigPanelOpen) {
					setIsConfigPanelOpen(() => false);
				}
				sidebarActions.toggleSidebar();
			},
			iconSpacing: 'spacious',
		},
	];

	const renderGraphError = () => {
		graphModalExperience.failure();
		return <GraphError />;
	};
	const renderForceGraph = () => (
		<AsyncForceGraph
			graphData={transformedGraphData}
			onNodeClick={onNodeClicked}
			onNodeDoubleClick={onNodeDoubleClicked}
			defaultSelectedNodeId={selectedNode.nodeId}
			isLayoutLocked={isGraphLayoutLocked}
		/>
	);

	const content = (
		<>
			<GraphHeader title={formatMessage(messages.title)} onClose={onClose} />
			<PushingSidebarContainer {...sidebarState}>
				<GraphWrapper>
					{/*
					 * Using Transition to delay the unmounting of the Spinner.
					 * We need to do this because the FadeIn around the AsyncForceGraph has a built in animation delay for fixing a visual bug.
					 * Adding the delay here compensates for the one inside FadeIn and prevents a flash of 'nothing' being rendered momentarily.
					 */}
					<Transition timeout={{ exit: FADEIN_ANIMATION_DELAY }} in={graphLoading} unmountOnExit>
						<SpinnerWrapper>
							<Spinner size="large" />
						</SpinnerWrapper>
					</Transition>

					{!graphLoading && <FadeIn>{graphError ? renderGraphError() : renderForceGraph()}</FadeIn>}

					<Actions>
						<GraphConfigurationActionPanel
							isConfigPanelOpen={isConfigPanelOpen}
							onOpenChange={(isOpen) => {
								// Only handle open events here. Close events are handled on the wrapper of this dialog because
								// otherwise clicks on the reference depth dropdown are considered "outside" this panel, and cause
								// the panel to close instead of updating the reference depth.
								isOpen && setIsConfigPanelOpen(true);
							}}
							menuPosition={sidebarState.isOpen ? 'left' : 'right'}
							renderActionButton={({ triggerRef, ...triggerProps }) => (
								<ActionsButton
									{...triggerProps}
									onClick={(event) => {
										if (isConfigPanelOpen) {
											setIsConfigPanelOpen(false);
										}
										triggerProps.onClick?.(event);
									}}
									triggerRef={triggerRef}
									Icon={CustomizeIcon}
									label={formatMessage(
										isConfigPanelOpen
											? messages.hideGraphConfigPanel
											: messages.showGraphConfigPanel,
									)}
									iconSpacing="spacious"
									testId="servicedesk-insight-object-graph.ui.graph-config-button"
								/>
							)}
						>
							<ConfigPanelHeader>
								{formatMessage(messages.graphConfigPanelHeader)}
							</ConfigPanelHeader>
							<ReferenceDepthConfigurationInputs
								maxOfSameType={maxOfSameType}
								referenceDepth={referenceDepth}
								onUpdateReferenceDepth={updateReferenceDepth}
								onUpdateMaxOfSameType={updateMaxOfSameType}
							/>
							{referenceTypes.length > 0 ? (
								<>
									<ReferenceTypeListHeader>
										{formatMessage(messages.referenceTypesHeader)}
									</ReferenceTypeListHeader>
									<ReferenceTypeToggleList
										referenceTypes={referenceTypes}
										onChange={updateReferenceTypes}
									/>
								</>
							) : null}
						</GraphConfigurationActionPanel>

						{actionButtons.map((action) => (
							<ActionsButton
								testId={`servicedesk-insight-object-graph.ui.button-${action.label}`}
								key={action.label}
								{...action}
							/>
						))}
					</Actions>
				</GraphWrapper>

				<PushingSidebar>
					<ObjectViewWrapper>
						{selectedNode.type === GRAPH_NODE_TYPES.OBJECT && (
							<ObjectView
								objectDetails={objectDetailsData}
								objectDetailsLoading={objectDetailsLoading}
								objectDetailsError={objectDetailsError}
								onRefreshButtonClick={handleFetchObjectDetails}
								selectedObjectId={toCmdbObjectId(selectedNode.referenceId)}
								onObjectAttributeClick={onObjectAttributeClick}
								source={source}
							/>
						)}
						{selectedNode.type === GRAPH_NODE_TYPES.OBJECT_TYPE && (
							<ObjectTypeView objectTypeNode={selectedNode} />
						)}
					</ObjectViewWrapper>
				</PushingSidebar>
			</PushingSidebarContainer>
		</>
	);

	return <Wrapper onClick={() => setIsConfigPanelOpen(false)}>{content}</Wrapper>;
};

export const InsightObjectGraphOrNull = (
	props: InsightObjectGraphOrNullProps | InsightObjectGraphProps,
) => {
	if (props.objectId !== undefined && props.objectTypeId !== undefined) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return <InsightObjectGraph {...(props as InsightObjectGraphProps)} />;
	}
	return null;
};
export const InsightObjectGraphModal = ({
	objectId,
	objectTypeId,
	onClose,
	source,
	testId,
	isObjectGraphOpen,
}: InsightObjectGraphOrNullProps) => (
	<ModalTransition>
		{isObjectGraphOpen && (
			<JSErrorBoundary
				id="InsightObjectGraph"
				packageName="jiraServiceDeskInsightObjectGraph"
				fallback="flag"
			>
				<ShortcutScope>
					<ModalDialog
						width={DIALOG_WIDTH}
						height={DIALOG_HEIGHT}
						onClose={onClose}
						autoFocus={false}
						stackIndex={1}
						testId={testId}
						messageId="servicedesk-insight-object-graph.ui.modal-dialog"
						messageType="transactional"
					>
						<InsightObjectGraphOrNull
							objectId={objectId}
							objectTypeId={objectTypeId}
							onClose={onClose}
							source={source}
							isObjectGraphOpen={isObjectGraphOpen}
						/>
					</ModalDialog>
				</ShortcutScope>
			</JSErrorBoundary>
		)}
	</ModalTransition>
);

export default InsightObjectGraphModal;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	height: 'calc(100% - 72px)',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ObjectViewWrapper = styled.div({
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.sunken'),
	overflowY: 'auto', // Enable vertical scrolling when needed
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderLeft: `1px solid ${token('color.border')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GraphWrapper = styled.div({
	position: 'relative',
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerWrapper = styled.div({
	/* Using position absolute because the SpinnerWrapper and graph get rendered at the same time momentarily. */
	/* This prevents the spinner from moving the graph position while they're both rendered */
	position: 'absolute',
	width: '100%',
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const fadeIn = keyframes({
	from: {
		opacity: 0,
	},
	to: {
		opacity: 1,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FadeIn = styled.div({
	opacity: 0,
	height: '100%',
	width: '100%',
	animationName: fadeIn,
	animationIterationCount: 1,
	/** We have a higher than normal animation delay here to prevent showing nodes flying around while the initial layout is calculated */
	animationDelay: `${FADEIN_ANIMATION_DELAY}ms`,
	animationDuration: '250ms',
	animationFillMode: 'forwards',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ConfigPanelHeader = styled.h2({
	marginBottom: token('space.300'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReferenceTypeListHeader = styled.h3({
	marginTop: token('space.300'),
});
